// src/pages/calidad/BpmFichaPersona.jsx
// Ventana con el historial de una persona en BPMs: saldo de puntos del
// trimestre, suspensiones e incidencias. Se usa desde el reporte y desde
// personal suspendido.
import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { FiDownload, FiX } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';

const PUNTOS_SUSPENSION = 12;
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const COLOR_NIVEL = { ALTO: ['#B23A48', '#FDF3F1'], MEDIO: ['#B2662A', '#FFF6E9'], BAJO: ['#3E7C5A', '#F0F7F1'] };

export const TagNivel = ({ nivel, puntos }) => {
  const [c, f] = COLOR_NIVEL[nivel] || ['#8A7690', '#F0E9E0'];
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 6, color: c, background: f, whiteSpace: 'nowrap' }}>
      {nivel}{puntos !== undefined ? ` · ${puntos} pts` : ''}
    </span>
  );
};

export default function BpmFichaPersona({ empleadoId, onCerrar }) {
  const [ficha, setFicha] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (empleadoId === null || empleadoId === undefined) return;
    setFicha(null);
    setError(null);
    api.get(`/api/calidad/bpm-analisis/persona/${empleadoId}`)
      .then((r) => setFicha(r.data.data))
      .catch((err) => { console.error(err); setError('No se pudo cargar la ficha.'); });
  }, [empleadoId]);

  if (empleadoId === null || empleadoId === undefined) return null;

  function excel() {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ficha.incidencias.map((i) => ({
      Fecha: fmtFecha(i.fecha), Turno: i.turno === 'dia' ? 'Día' : 'Noche', Área: i.area, Incidencia: i.incidencia,
      Nivel: i.nivel_riesgo, Puntos: i.puntuacion, Observaciones: i.observaciones || '',
    }))), 'Incidencias');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ficha.suspensiones.map((x) => ({
      'Fecha de suspensión': fmtFecha(x.fecha_suspension), Puntos: x.puntos_en_suspension, Estatus: x.estatus, 'Cumplida el': fmtFecha(x.fecha_cumplida),
    }))), 'Suspensiones');
    XLSX.writeFile(wb, `BPM_${(ficha.nombre || 'persona').replace(/[^\w]+/g, '_')}.xlsx`);
  }

  const saldo = ficha ? Math.max(0, ficha.saldo_trimestre) : 0;
  const colorSaldo = saldo >= 8 ? '#B23A48' : saldo >= 4 ? '#B8862E' : '#3E7C5A';

  return (
    <div style={s.modalFondo} onClick={onCerrar}>
      <div style={{ ...s.modalCaja, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitulo}>{ficha?.nombre || 'Cargando…'}</div>
            {ficha && (
              <div style={{ fontSize: 12, color: '#8A7690', marginTop: 2 }}>
                {ficha.area || 'Sin área'} {ficha.manual && '· agregada a mano (no está en Asistencias)'}
              </div>
            )}
          </div>
          <button style={s.modalCerrarBtn} onClick={onCerrar} aria-label="Cerrar"><FiX /></button>
        </div>

        {error && <div style={{ color: '#B23A48', fontSize: 13 }}>{error}</div>}
        {ficha && (
          <>
            <div style={s.modalContexto}>
              <div style={s.modalContextoGrid}>
                <div>
                  <div style={s.modalContextoLbl}>Saldo del trimestre</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: colorSaldo }}>{saldo} <span style={{ fontSize: 12, color: '#8A7690' }}>/ {PUNTOS_SUSPENSION} pts</span></div>
                </div>
                <div>
                  <div style={s.modalContextoLbl}>Puntos del trimestre</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#2A1830' }}>{ficha.puntos_trimestre}</div>
                </div>
                <div>
                  <div style={s.modalContextoLbl}>Suspensiones (total)</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: ficha.suspensiones.length ? '#B23A48' : '#2A1830' }}>{ficha.suspensiones.length}</div>
                </div>
              </div>
              <div style={{ height: 8, background: '#F0E9E0', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, (saldo / PUNTOS_SUSPENSION) * 100)}%`, background: colorSaldo }} />
              </div>
              <div style={{ fontSize: 11, color: '#8A7690', marginTop: 4 }}>
                Trimestre {fmtFecha(ficha.trimestre.inicio)} – {fmtFecha(ficha.trimestre.fin)}.
                {saldo < PUNTOS_SUSPENSION ? ` Le faltan ${PUNTOS_SUSPENSION - saldo} pts para una suspensión.` : ''}
              </div>
            </div>

            {ficha.suspensiones.length > 0 && (
              <>
                <div style={s.modalSeccionTitulo}>Suspensiones</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                  {ficha.suspensiones.map((x) => (
                    <div key={x.id} style={{ fontSize: 12.5, display: 'flex', gap: 10 }}>
                      <span>{fmtFecha(x.fecha_suspension)}</span>
                      <span style={{ fontWeight: 600, color: x.estatus === 'activa' ? '#B23A48' : '#3E7C5A' }}>
                        {x.estatus === 'activa' ? 'Activa' : `Cumplida ${fmtFecha(x.fecha_cumplida)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ ...s.headerFila, marginBottom: 6 }}>
              <div style={s.modalSeccionTitulo}>Incidencias ({ficha.incidencias.length})</div>
              <button onClick={excel} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Excel</button>
            </div>
            <div style={{ ...s.tablaScroll, maxHeight: 320, overflowY: 'auto' }}>
              <table style={s.tabla}>
                <thead>
                  <tr>
                    <th style={s.th}>Fecha</th>
                    <th style={s.th}>Turno</th>
                    <th style={s.th}>Incidencia</th>
                    <th style={s.th}>Nivel</th>
                  </tr>
                </thead>
                <tbody>
                  {ficha.incidencias.length === 0 && <tr><td colSpan={4} style={{ ...s.td, color: '#8A7690' }}>Sin incidencias.</td></tr>}
                  {ficha.incidencias.map((i) => (
                    <tr key={i.id}>
                      <td style={s.td}>{fmtFecha(i.fecha)}</td>
                      <td style={s.td}>{i.turno === 'dia' ? 'Día' : 'Noche'}</td>
                      <td style={{ ...s.td, whiteSpace: 'normal' }}>
                        {i.incidencia}
                        {i.observaciones && <div style={{ fontSize: 11, color: '#8A7690', fontStyle: 'italic' }}>{i.observaciones}</div>}
                      </td>
                      <td style={s.td}><TagNivel nivel={i.nivel_riesgo} puntos={i.puntuacion} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}